import {
  ModelMessage,
  QuestionnaireData,
  ResponseMessage,
  SendMessageResponse,
  SendMessageToAIAgentBody,
  StreamMessageResponse
} from '../types';
import axios, {AxiosInstance, AxiosResponse} from 'axios';
import {v4 as guid} from 'uuid';

export class AIAgentService {
  private readonly axios: AxiosInstance;
  private readonly sessionId: string;
  static API_BASE = process.env.REACT_APP_AI_AGENT_API_SERVER;
  static URL = {
    chat: '/chat',
  };
  public static chatController: AbortController | null = null;

  constructor() {
    this.axios = axios.create({
      baseURL: AIAgentService.API_BASE,
      headers: {
        'Content-Type': 'application/json',
      }
    });
    this.sessionId = guid();
  }

  public async call(userPrompt: string, questionnaireData?: QuestionnaireData) {
    try {
      const data = await this.sendMessage(
        questionnaireData ? '' : userPrompt,
        this.sessionId,
        questionnaireData
      );

      return data
        .filter(this.isModelMessage)
        .map(msg => msg.data.data)
        .filter(Boolean)
        .join('')
        .replaceAll(/<\/?thinking>[\w\s\S]*<\/?thinking>/gim, '')
        .replace(/^---/i, '')
        .trim();
    } catch (e) {
      if (e instanceof Error) {
        this.handleChatError(e);
      }
      return '';
    }
  };

  private async sendMessage(
    userPrompt: string,
    sessionId: string,
    questionnaireData?: {
      activities: string[],
      time_of_year: string,
      region: string,
      kids_12: boolean,
      babies: boolean,
      dogs: boolean,
      trails_options: string,
      meal_options: string,
    }
  ) {
    const requestBody: SendMessageToAIAgentBody = {
      user_prompt: userPrompt,
      session_id: sessionId
    };
    if (questionnaireData) {
      requestBody.questionnaire_data = questionnaireData;
    }

    AIAgentService.chatController = new AbortController();

    const response = await this.axios.post<
      SendMessageResponse,
      AxiosResponse<SendMessageResponse>,
      SendMessageToAIAgentBody
    >(
      AIAgentService.URL.chat,
      requestBody,
      {
        responseType: 'stream',
        responseEncoding: 'utf-8',
        signal: AIAgentService.chatController.signal,
      }
    );

    if (response.data) {
      return JSON.parse(`[${response.data.replaceAll(/}\s*{/gi, '},{')}]`) as ResponseMessage[];
    } else {
      throw new Error();
    }
  }

  public async stream(userPrompt: string, onChunk: (chunk: string) => Promise<void>, questionnaireData?: QuestionnaireData) {
    try {
      const stream = await this.streamMessage(
        questionnaireData ? '' : userPrompt,
        this.sessionId,
        questionnaireData
      );

      let text = '';
      const decoder = new TextDecoder();
      for await (const encodedChunk of (stream as unknown as ArrayBuffer[])) {
        const decodedChunk = decoder.decode(encodedChunk);
        const chunk = JSON.parse(decodedChunk) as ResponseMessage;
        if (this.isModelMessage(chunk)) {
          for (const char of chunk.data.data) {
            text += char;
            await onChunk(text);
            await new Promise(resolve => setTimeout(resolve, 10));
          }
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        this.handleChatError(e);
      }
    }
  };

  private async streamMessage(
    userPrompt: string,
    sessionId: string,
    questionnaireData?: {
      activities: string[],
      time_of_year: string,
      region: string,
      kids_12: boolean,
      babies: boolean,
      dogs: boolean,
      trails_options: string,
      meal_options: string,
    }
  ) {
    const requestBody: SendMessageToAIAgentBody = {
      user_prompt: userPrompt,
      session_id: sessionId
    };
    if (questionnaireData) {
      requestBody.questionnaire_data = questionnaireData;
    }

    AIAgentService.chatController = new AbortController();

    const response = await this.axios.post<
      StreamMessageResponse,
      AxiosResponse<StreamMessageResponse>,
      SendMessageToAIAgentBody
    >(
      AIAgentService.URL.chat,
      requestBody,
      {
        responseType: 'stream',
        responseEncoding: 'utf-8',
        signal: AIAgentService.chatController.signal,
        adapter: 'fetch'
      }
    );

    if (response.data) {
      return response.data;
    } else {
      throw new Error();
    }
  }

  private handleChatError(error: Error) {
    console.log({error});
    if (error.message.includes('Failed to fetch')) {
      console.error(('Unable to connect to the server. Please check your internet connection.'));
    } else if (error.message.includes('400')) {
      console.error('Invalid request. Please check your input and try again.');
    } else if (error.message.includes('429')) {
      console.error('Too many requests. Please wait a moment before trying again.');
    } else if (error.message.includes('500')) {
      console.error('Server error. Please try again later.');
    } else {
      console.error('An unexpected error occurred. Please try again.');
    }
  };

  private isModelMessage = (message: ResponseMessage): message is ModelMessage => {
    return message.type === 'model_message';
  };
}
